import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { feedApi, type FeedRequest } from "../api";
import type { FeedPage, HomeFeedPage, PostResponse } from "../types/generated";
import {
  getFeedCacheAuthorityRevision,
  reconcileConfirmedFeedPins,
  reconcileConfirmedHomePins,
} from "./feedCache";
import { useBoundedFeedFreshness } from "./feedFreshness";
import { feedQueryKeys, homeFeedQueryKeys } from "./feedKeys";
import { recordFeedFreshnessTelemetry } from "./feedTelemetry";

export { feedQueryKeys, homeFeedQueryKeys } from "./feedKeys";

function flattenPosts(
  data: InfiniteData<FeedPage> | undefined,
): PostResponse[] {
  const seen = new Set<string>();
  const posts: PostResponse[] = [];
  for (const page of data?.pages ?? []) {
    for (const post of page.items) {
      if (!seen.has(post.post_id)) {
        seen.add(post.post_id);
        posts.push(post);
      }
    }
  }
  return posts;
}

export interface UseFeedOptions {
  enabled?: boolean;
  freshness?: {
    enabled?: boolean;
    intervalMs?: number;
  };
}

interface CursorPage {
  next_cursor?: string | null;
  has_more: boolean;
}

const MAX_EMPTY_CONTINUATION_PAGES = 20;

function currentFeedLifecycleActive(): boolean {
  return (
    (typeof document === "undefined" ||
      document.visibilityState === "visible") &&
    (typeof navigator === "undefined" || navigator.onLine !== false)
  );
}

function useFeedLifecycleActive(): boolean {
  const [active, setActive] = useState(currentFeedLifecycleActive);
  useEffect(() => {
    const update = () => setActive(currentFeedLifecycleActive());
    document.addEventListener("visibilitychange", update);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      document.removeEventListener("visibilitychange", update);
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return active;
}

function useBoundedEmptyContinuation<
  Page extends { items: readonly unknown[]; has_more: boolean },
>(input: {
  data: InfiniteData<Page> | undefined;
  signature: string;
  enabled: boolean;
  isFetching: boolean;
  isError: boolean;
  fetchNext: () => Promise<unknown>;
}): void {
  const progress = useRef({ signature: input.signature, count: 0 });
  useEffect(() => {
    if (progress.current.signature !== input.signature) {
      progress.current = { signature: input.signature, count: 0 };
    }
    const last = input.data?.pages.at(-1);
    if (!input.enabled) return;
    if (!last || last.items.length > 0 || !last.has_more) {
      progress.current.count = 0;
      return;
    }
    if (
      input.isFetching ||
      input.isError ||
      progress.current.count >= MAX_EMPTY_CONTINUATION_PAGES
    ) {
      return;
    }
    progress.current.count += 1;
    void input.fetchNext();
  }, [
    input.data,
    input.enabled,
    input.fetchNext,
    input.isError,
    input.isFetching,
    input.signature,
  ]);
}

async function loadFreshWindow<Page extends CursorPage>(
  head: Page,
  pageCount: number,
  loadPage: (cursor: string) => Promise<Page>,
): Promise<InfiniteData<Page>> {
  const pages = [head];
  const pageParams: Array<string | null> = [null];
  while (pages.length < Math.max(1, pageCount)) {
    const previous = pages[pages.length - 1];
    if (!previous?.has_more || !previous.next_cursor) break;
    pageParams.push(previous.next_cursor);
    pages.push(await loadPage(previous.next_cursor));
  }
  return { pages, pageParams };
}

type HomeFeedItem = HomeFeedPage["items"][number];

function homeItemIdentity(item: HomeFeedItem): string {
  if (item.kind === "post") return `post:${item.post.post_id}`;
  if (item.kind === "ranked_match_completed") {
    return `ranked_match_completed:${item.activity.activity_id}`;
  }
  const exhaustive: never = item;
  return exhaustive;
}

export function flattenHomeItems(
  data: InfiniteData<HomeFeedPage> | undefined,
): HomeFeedItem[] {
  const seen = new Set<string>();
  const items: HomeFeedItem[] = [];
  for (const page of data?.pages ?? []) {
    for (const item of page.items) {
      const identity = homeItemIdentity(item);
      if (!seen.has(identity)) {
        seen.add(identity);
        items.push(item);
      }
    }
  }
  return items;
}

export function useHomeFeed(
  request: { limit?: number } = {},
  options: UseFeedOptions = {},
) {
  const queryClient = useQueryClient();
  const lifecycleActive = useFeedLifecycleActive();
  const queryKey = homeFeedQueryKeys.list(request);
  const requestSignature = JSON.stringify(queryKey);
  const requestRef = useRef(request);
  requestRef.current = request;
  const freshnessEnabled = options.freshness?.enabled ?? false;
  const freshnessInterval = options.freshness?.intervalMs ?? 30_000;
  const freshnessQueryKey = useMemo(
    () => ["home-feed-freshness", requestSignature] as const,
    [requestSignature],
  );
  const loadFreshHead = useCallback(
    async (signal: AbortSignal) => {
      try {
        const page = await feedApi.getHomePage(requestRef.current, null, {
          signal,
        });
        const currentIds = new Set(
          flattenHomeItems(
            queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey),
          ).map(homeItemIdentity),
        );
        recordFeedFreshnessTelemetry(
          page.items.some((item) => !currentIds.has(homeItemIdentity(item)))
            ? "new_posts"
            : "unchanged",
        );
        return page;
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          recordFeedFreshnessTelemetry("failed");
        }
        throw error;
      }
    },
    [queryClient, requestSignature],
  );
  const freshness = useBoundedFeedFreshness<HomeFeedPage>({
    queryClient,
    queryKey: freshnessQueryKey,
    registryKey: `home:${requestSignature}:${freshnessInterval}`,
    enabled: freshnessEnabled && (options.enabled ?? true),
    intervalMs: freshnessInterval,
    loadHead: loadFreshHead,
  });
  const applyFreshness = freshness.apply;
  const refreshNewPosts = useCallback(
    () =>
      applyFreshness(async (head, signal) => {
        await queryClient.cancelQueries({ queryKey, exact: true });
        const current =
          queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey);
        if (!current?.pages.length) return false;
        const authorityRevision = getFeedCacheAuthorityRevision(
          queryClient,
          queryKey,
        );
        const installed = await loadFreshWindow(
          head,
          current.pages.length,
          (cursor) =>
            feedApi.getHomePage(requestRef.current, cursor, { signal }),
        );
        if (
          queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey) !==
            current ||
          getFeedCacheAuthorityRevision(queryClient, queryKey) !==
            authorityRevision
        ) {
          return false;
        }
        queryClient.setQueryData<InfiniteData<HomeFeedPage>>(
          queryKey,
          reconcileConfirmedHomePins(queryClient, queryKey, installed),
        );
        return true;
      }),
    [applyFreshness, queryClient, requestSignature],
  );
  const currentIds = new Set(
    flattenHomeItems(
      queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey),
    ).map(homeItemIdentity),
  );
  const newPostsAvailable = Boolean(
    freshness.head?.items.some(
      (item) => !currentIds.has(homeItemIdentity(item)),
    ),
  );
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      feedApi.getHomePage(request, pageParam as string | null, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (page: HomeFeedPage) =>
      page.has_more ? page.next_cursor : undefined,
    enabled: options.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
  const fetchEmptyNext = useCallback(
    () => query.fetchNextPage({ cancelRefetch: false }),
    [query.fetchNextPage],
  );
  useBoundedEmptyContinuation({
    data: query.data,
    signature: requestSignature,
    enabled: (options.enabled ?? true) && lifecycleActive,
    isFetching: query.isFetchingNextPage,
    isError: query.isFetchNextPageError,
    fetchNext: fetchEmptyNext,
  });
  return {
    ...query,
    checkFreshness: freshness.check,
    freshnessError: freshness.error,
    isCheckingFreshness: freshness.checking,
    isApplyingFreshness: freshness.applying,
    newPostsAvailable,
    refreshNewPosts,
    get hasNextPage() {
      const pages =
        queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey)?.pages;
      if (!pages?.length) return undefined;
      return pages[pages.length - 1]?.has_more === true;
    },
    get items() {
      return flattenHomeItems(
        queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey),
      );
    },
    get partialSources() {
      const pages =
        queryClient.getQueryData<InfiniteData<HomeFeedPage>>(queryKey)?.pages;
      return Array.from(
        new Set(pages?.flatMap((page) => page.partial_sources) ?? []),
      );
    },
  };
}

export function useFeed(request: FeedRequest, options: UseFeedOptions = {}) {
  const queryClient = useQueryClient();
  const lifecycleActive = useFeedLifecycleActive();
  const queryKey = feedQueryKeys.list(request);
  const requestSignature = JSON.stringify(queryKey);
  const freshnessQueryKey = useMemo(
    () => ["feed-freshness", requestSignature] as const,
    [requestSignature],
  );
  const requestRef = useRef(request);
  requestRef.current = request;
  const freshnessEnabled = options.freshness?.enabled ?? false;
  const freshnessInterval = options.freshness?.intervalMs ?? 30_000;
  const loadFreshHead = useCallback(
    async (signal: AbortSignal) => {
      try {
        const page = await feedApi.getPage(requestRef.current, null, {
          signal,
        });
        const currentIds = new Set(
          flattenPosts(
            queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey),
          ).map((post) => post.post_id),
        );
        recordFeedFreshnessTelemetry(
          page.items.some((post) => !currentIds.has(post.post_id))
            ? "new_posts"
            : "unchanged",
        );
        return page;
      } catch (error) {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          recordFeedFreshnessTelemetry("failed");
        }
        throw error;
      }
    },
    [queryClient, requestSignature],
  );
  const freshness = useBoundedFeedFreshness<FeedPage>({
    queryClient,
    queryKey: freshnessQueryKey,
    registryKey: `posts:${requestSignature}:${freshnessInterval}`,
    enabled: freshnessEnabled && (options.enabled ?? true),
    intervalMs: freshnessInterval,
    loadHead: loadFreshHead,
  });
  const applyFreshness = freshness.apply;
  const refreshNewPosts = useCallback(
    () =>
      applyFreshness(async (head, signal) => {
        await queryClient.cancelQueries({ queryKey, exact: true });
        const current =
          queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey);
        if (!current?.pages.length) return false;
        const authorityRevision = getFeedCacheAuthorityRevision(
          queryClient,
          queryKey,
        );
        const installed = await loadFreshWindow(
          head,
          current.pages.length,
          (cursor) => feedApi.getPage(requestRef.current, cursor, { signal }),
        );
        if (
          queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey) !==
            current ||
          getFeedCacheAuthorityRevision(queryClient, queryKey) !==
            authorityRevision
        ) {
          return false;
        }
        queryClient.setQueryData<InfiniteData<FeedPage>>(
          queryKey,
          reconcileConfirmedFeedPins(queryClient, queryKey, installed),
        );
        return true;
      }),
    [applyFreshness, queryClient, requestSignature],
  );
  const currentIds = new Set(
    flattenPosts(
      queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey),
    ).map((post) => post.post_id),
  );
  const newPostsAvailable = Boolean(
    freshness.head?.items.some((post) => !currentIds.has(post.post_id)),
  );
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam, signal }) =>
      feedApi.getPage(request, pageParam as string | null, { signal }),
    initialPageParam: null as string | null,
    getNextPageParam: (page: FeedPage) =>
      page.has_more ? page.next_cursor : undefined,
    enabled: options.enabled ?? true,
    // Freshness is deliberately owned by the bounded head probe above.  Letting
    // TanStack refetch every loaded infinite page on focus/reconnect would
    // replace the user's visible list and make read cost grow with history.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
  });
  const fetchEmptyNext = useCallback(
    () => query.fetchNextPage({ cancelRefetch: false }),
    [query.fetchNextPage],
  );
  useBoundedEmptyContinuation({
    data: query.data,
    signature: requestSignature,
    enabled: (options.enabled ?? true) && lifecycleActive,
    isFetching: query.isFetchingNextPage,
    isError: query.isFetchNextPageError,
    fetchNext: fetchEmptyNext,
  });

  return {
    ...query,
    checkFreshness: freshness.check,
    freshnessError: freshness.error,
    isCheckingFreshness: freshness.checking,
    isApplyingFreshness: freshness.applying,
    newPostsAvailable,
    refreshNewPosts,
    get hasNextPage() {
      const pages =
        queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey)?.pages;
      if (!pages?.length) return undefined;
      return pages[pages.length - 1]?.has_more === true;
    },
    get posts() {
      return flattenPosts(
        queryClient.getQueryData<InfiniteData<FeedPage>>(queryKey),
      );
    },
  };
}
